import Link from '@/components/Link';
import Ad, { clearKeyValuePair, setKeyValuePair } from '@gik/ads/components/Ad';
import ZonesMap from '@gik/ads/maps/ZonesMap';
import { usePosts } from '@gik/api/posts/posts';
import { usePostsRelated } from '@gik/api/posts/postsRelated';
import type { RelationFilter } from '@gik/api/posts/PostsUrlBuilder';
import { ArticleBody } from '@gik/blog/components/ArticleBody/ArticleBody';
import { ArticleHeaderSection } from '@gik/blog/components/ArticleHeaderSection/ArticleHeaderSection';
import { ArticlesGrid } from '@gik/blog/components/ArticlesGrid/ArticlesGrid';
import type Article from '@gik/blog/models/Article';
import { withBlogArticlePageMetaTags } from '@gik/blog/utils/MetaTags';
import WordpressConverter from '@gik/blog/utils/WordpressConverter';
import { DynamicComponent } from '@gik/core/components/DynamicComponent';
import { withTitle } from '@gik/core/hocs/withTitle';
import type WordpressPost from '@gik/core/models/wordpress/WordpressPost';
import { NotFoundPage } from '@gik/core/pages/NotFound/NotFoundPage';
import { useBemCN } from '@gik/core/utils/bemBlock';
import getPreviewMode, { PreviewModeType } from '@gik/core/utils/PreviewMode';
import { ArticleStructuredData } from '@gik/seo';
import { ArticleAuthorLink } from '@gik/ui/gik/ArticleAuthorLink';
import { PageSection } from '@gik/ui/gik/PageSection';
import { Separator } from '@gik/ui/Separator';
import React from 'react';

type InnerArticlePageServerProps = {
  relatedArticles: WordpressPost[];
};

export type ArticlePageProps = {
  article: Article;
  fallbackData?: ArticlePageServerProps;
};

const blockName = 'article-page';

function ArticlePageComp({ article, fallbackData }: ArticlePageProps) {
  const bem = useBemCN(blockName);
  const blogPreviewMode = getPreviewMode(PreviewModeType.Blog);

  React.useEffect(() => {
    if (article?.taxonomyItemName) {
      setKeyValuePair(article.taxonomyItemName);
    }

    return () => {
      clearKeyValuePair();
    };
  }, [article?.taxonomyItemName]);

  if (article?.isPreviewOnly && !blogPreviewMode) {
    return <NotFoundPage />;
  }

  // FIXME: need to add reading progress
  return withBlogArticlePageMetaTags(
    article,
    <ArticleStructuredData article={article}>
      <article {...bem()}>
        <ArticleHeaderSection article={article} />

        <PageSection {...bem('article-body')} noPadTop>
          <div {...bem('side-column', [{ left: true }])} />
          <main {...bem('main')}>
            <div {...bem('author-link')}>
              <ArticleAuthorLink
                authorFullName={article?.authorFullName}
                authorSlug={article?.authorSlug}
                articleId={article?.articleId}
                date={article?.date}
              />
            </div>

            <Separator variant={'neutral-300'} thickness={'3x'} size={'lg'} />

            {article && <ArticleBody article={article} />}

            <Separator variant={'neutral-300'} thickness={'3x'} size={'lg'} />

            <p {...bem('terms')}>
              Give InKind does not provide medical advice, diagnosis, or treatment. We have an affiliate relationship
              with many of the advertisers on our site, and may receive a commission from any products purchased from
              links in this article. See <Link href="/terms-service">Terms {'&'} Conditions</Link>.
            </p>

            <Ad
              zone={ZonesMap.ArticleZones.Bottom}
              mobileZone={ZonesMap.ArticleZones.BottomMobile}
              override={article?.advertising}
            />
          </main>
          <div {...bem('side-column', [{ right: true }])}>
            <Ad
              zone={ZonesMap.ArticleZones.Right}
              {...bem('columns-3')}
              adType="vertical"
              override={article?.advertising}
            />
          </div>
        </PageSection>

        <PageSection>
          <DynamicComponent<WordpressPost[], Article[]>
            height={480}
            fetch={function useFetch() {
              return usePostsRelated(
                {
                  relationFilters: [
                    {
                      taxonomy: article?.primaryTaxonomySlug,
                      terms: [article?.taxonomyItemSlug],
                    },
                  ] as RelationFilter<string[]>[],
                },
                {
                  fallbackData: fallbackData?.relatedArticles,
                }
              );
            }}
            transform={data =>
              data.map(WordpressConverter.convertPost).filter(WordpressConverter.filterOutPreviewContent)
            }
            loadingComponent={() => (
              <ArticlesGrid title={'Related Articles'} articles={Array.from(new Array(4))} skeleton />
            )}
          >
            {({ transformed: articles }) => (
              <ArticlesGrid
                articles={articles}
                title={'Related Articles'}
                filter={articles =>
                  articles
                    ?.filter(_article => !_article.isPreviewOnly || _article.isPreviewOnly === blogPreviewMode)
                    .filter(_article => _article.articleSlug !== article.articleSlug)
                    .slice(0, 4)
                }
              />
            )}
          </DynamicComponent>
        </PageSection>
      </article>
    </ArticleStructuredData>
  );
}

export type ArticlePageServerProps = InnerArticlePageServerProps & {
  article: WordpressPost[];
};

export type ArticlePageWithDataProps = {
  slug: string;
  fallbackData?: ArticlePageServerProps;
} & Omit<ArticlePageProps, 'fallbackData' | 'article'>;

export function ArticlePageWithData({ slug, fallbackData }: ArticlePageWithDataProps) {
  return (
    <DynamicComponent<WordpressPost[], Article>
      height={2000}
      fetch={function useFetch() {
        return usePosts(
          {
            slug,
            perPage: 1,
          },
          {
            fallbackData: fallbackData?.article,
          }
        );
      }}
      transform={data => WordpressConverter.convertPost(data[0])}
    >
      {({ transformed: article }) => {
        return withTitle(
          {
            title: article?.title,
            fallback: fallbackData?.article?.[0]?.title?.rendered,
          },
          <ArticlePageComp article={article} fallbackData={fallbackData} />
        );
      }}
    </DynamicComponent>
  );
}
